<template>
  <div class="profile-settings">
    <div class="profile-settings__row">
      <label for="homePage">Home Page & Preferred Team</label>
      <ElementSelect
        v-model="homePage"
        placeholder="Select default home page"
        class="profile-settings__select-homepage"
      >
        <ElementOption
          label="Styles"
          value="style"
        />
        <ElementOption
          label="Blocks"
          value="block"
        />
        <ElementOption
          label="Trims"
          value="trim"
        />
        <ElementOption
          label="Fabrics"
          value="fabric"
        />
        <ElementOption
          label="Stitches"
          value="stitch"
        />
        <ElementOption
          label="Colors"
          value="color"
        />
      </ElementSelect>
      <ElementSelect
        v-model="teamPreference"
        placeholder="Select default team"
        class="profile-settings__select-team"
      >
        <ElementOption
          label="ALL"
          value="ALL"
        />
        <ElementOption
          v-for="team in availableTeams"
          :key="team.id"
          :label="team.name"
          :value="team.id"
        />
      </ElementSelect>
    </div>

    <div class="profile-settings__row">
      <label for="timezone">Timezone</label>
      <ElementSelect
        v-model="timezone"
        placeholder="Select a timezone"
        filterable
        class="profile-settings__select-timezone"
      >
        <ElementOption
          v-for="tz in TIMEZONES"
          :key="tz.id"
          :label="tz.label"
          :value="tz.value"
        />
      </ElementSelect>
      <ElementButton
        class="profile-settings__button-timezone"
        @click="setCurrentTimezone"
      >
        Set to Current Timezone
      </ElementButton>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { TIMEZONES } from '@/constants/timezones'

export default {
  name: 'ProfileSettings',

  data () {
    return {
      TIMEZONES
    }
  },

  computed: {
    ...mapGetters({
      profileTimezone: 'getProfileTimezone',
      profileHomePage: 'getProfileHomePage',
      profileTeam: 'getProfileTeam',
      availableTeams: 'getUserTeams',
      user: 'getCognitoUserData'
    }),

    /**
     * Computed property for home page preference with info message.
     */
    homePage: {
      /**
       * Retrieve the current home page preference from the store.
       *
       * @returns {string}
       */
      get () {
        return this.profileHomePage || 'style'
      },

      /**
       * Update the home page preference in the backend and store.
       *
       * @param   {string} value - The new home page value.
       * @returns {void}
       */
      set (value) {
        this.updateProfileSettings({ homePage: value })
        this.$message({
          showClose: true,
          message: `Home page set to ${value}`,
          duration: 4000
        })
      }
    },

    /**
     * Computed property for team preference with info message.
     */
    teamPreference: {
      /**
       * Retrieve the current team preference from the store.
       *
       * @returns {string}
       */
      get () {
        return this.profileTeam || 'ALL'
      },

      /**
       * Update the team preference in the backend and store.
       *
       * @param   {string} value - The new team value.
       * @returns {void}
       */
      set (value) {
        this.updateProfileSettings({ team: value })
        const team = this.availableTeams.find(t => t.id === value)
        const teamLabel = team ? team.name : value
        this.$message({
          showClose: true,
          message: `Default team set to ${teamLabel}`,
          duration: 4000
        })
      }
    },

    /**
     * Computed property for timezone with info message.
     */
    timezone: {
      /**
       * Retrieve the current profile timezone from the store.
       *
       * @returns {string}
       */
      get () {
        return this.profileTimezone
      },

      /**
       * Update the profile timezone in the backend and store.
       *
       * @param   {string} value - The new timezone value.
       * @returns {void}
       */
      set (value) {
        this.updateProfileSettings({ timezone: value })

        this.$message({
          showClose: true,
          message: `Timezone ${value} has been successfully set`,
          duration: 4000
        })
      }
    }
  },

  mounted () {
    this.fetchProfileSettings()
  },

  methods: {
    ...mapActions(['fetchProfileSettings', 'updateProfileSettings']),

    /**
     * Sets the timezone to the user's current system timezone,
     * if it's present in the list of available timezones.
     *
     * @returns {void}
     */
    setCurrentTimezone () {
      this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  }
}
</script>

<style lang="scss" scoped>
$color-text-default: #333;
$color-surface: #fff;
$color-border-default: #edebeb;
$font-size-sm: 14px;
$font-size-m: 16px;
$border-radius-sm: 8px;
$label-min-width: 240px;
$homepage-width: 180px;
$team-width: 220px;
$timezone-width: 500px;

.profile-settings {
  display: flex;
  flex-direction: column;
  gap: spacing(3);
  margin-bottom: spacing(6);
  margin-left: spacing(1.5);
  padding: spacing(4);
  color: $color-text-default;
  font-size: $font-size-sm;
  font-family: arial, sans-serif;
  background-color: $color-surface;
  border: 1px solid $color-border-default;
  border-radius: $border-radius-sm;
}

.profile-settings__row {
  display: flex;
  gap: spacing(1);
  align-items: center;

  label {
    min-width: $label-min-width;
    margin-bottom: 0;
    font-weight: $font-weight-medium;
    font-size: $font-size-m;
  }
}

.profile-settings__select-homepage {
  width: $homepage-width;
}

.profile-settings__select-team {
  width: $team-width;
}

.profile-settings__select-timezone {
  width: $timezone-width;
}

.profile-settings__button-timezone {
  margin-left: spacing(1);
}
</style>
